import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { UserPlus, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["OWNER", "DENTIST", "FRONT_DESK", "ASSISTANT", "ACCOUNTANT"];
const roleColor = {
  OWNER: "bg-[#0F4C3A] text-white",
  DENTIST: "bg-[#1E3A8A] text-white",
  FRONT_DESK: "bg-[#D69E2E] text-white",
  ASSISTANT: "bg-[#4338CA] text-white",
  ACCOUNTANT: "bg-[#15803D] text-white",
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "DENTIST" });

  const load = async () => {
    try { setUsers((await api.get("/users")).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== "OWNER") {
    return (
      <div className="p-10 max-w-md">
        <div className="bg-white border border-[#E5E5E2] rounded-md p-6">
          <Shield className="w-6 h-6 text-[#0F4C3A] mb-2" />
          <div className="font-display text-lg font-bold">Restricted</div>
          <div className="text-sm text-[#52525B] mt-1">Only clinic owners can manage staff accounts.</div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success(`${form.role.replace("_"," ")} added`);
      setOpen(false); setForm({ email: "", name: "", password: "", role: "DENTIST" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const label = "text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1 block";
  const inp = "w-full bg-white border border-[#E5E5E2] text-sm px-3 py-2 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0F4C3A]";

  return (
    <div className="p-8 max-w-[1200px] mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#E27B58] font-semibold mb-1">Team</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Staff Accounts</h1>
          <p className="text-sm text-[#52525B] mt-1">{users.length} users · role-based access</p>
        </div>
        <button data-testid="add-user-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-[#0F4C3A] text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-[#16654F]">
          <UserPlus className="w-4 h-4" /> Add staff
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E2] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#52525B] bg-[#FDFDFB]">
              <th className="p-3 border-b border-[#E5E5E2]">Name</th>
              <th className="p-3 border-b border-[#E5E5E2]">Email</th>
              <th className="p-3 border-b border-[#E5E5E2]">Role</th>
              <th className="p-3 border-b border-[#E5E5E2]">Since</th>
              <th className="p-3 border-b border-[#E5E5E2]"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-[#FDFDFB]" data-testid={`user-row-${u.email}`}>
                <td className="p-3 border-b border-[#E5E5E2] font-medium">{u.name}</td>
                <td className="p-3 border-b border-[#E5E5E2] text-[#52525B]">{u.email}</td>
                <td className="p-3 border-b border-[#E5E5E2]">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm ${roleColor[u.role] || "bg-[#F4F4F5]"}`}>{u.role.replace("_"," ")}</span>
                </td>
                <td className="p-3 border-b border-[#E5E5E2] text-[#52525B]">{new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                <td className="p-3 border-b border-[#E5E5E2] text-right">
                  {u.id !== user.id && (
                    <button onClick={() => remove(u)} className="text-[#D93838] hover:bg-[#FEE2E2] rounded-sm p-1.5"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={submit} className="w-full max-w-md bg-white rounded-md shadow-2xl">
            <div className="px-6 py-4 border-b border-[#E5E5E2] flex justify-between items-center">
              <h3 className="font-display text-lg font-bold">Add staff member</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-sm text-[#52525B]">Close</button>
            </div>
            <div className="p-6 space-y-3">
              <div><label className={label}>Name *</label><input required data-testid="user-name" className={inp} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div><label className={label}>Email *</label><input required type="email" data-testid="user-email" className={inp} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div><label className={label}>Temporary password *</label><input required type="text" data-testid="user-password" className={inp} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} /></div>
              <div>
                <label className={label}>Role *</label>
                <select data-testid="user-role" className={inp} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                  {ROLES.map((r) => <option key={r} value={r}>{r.replace("_"," ")}</option>)}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E5E5E2] flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2 border border-[#E5E5E2] rounded-md">Cancel</button>
              <button type="submit" data-testid="save-user-btn" className="text-sm px-4 py-2 bg-[#0F4C3A] text-white rounded-md hover:bg-[#16654F]">Add</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
